import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Clear, Add } from '@mui/icons-material';
import { Grid, TextField, InputAdornment, IconButton } from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import * as colors from 'commons/styles/colors';
import { STOItem, STOProcessedItem } from 'commons/types';
import {
  getSTOPickProductDetail,
  postSTOPickedItem,
  postSTOPickRemoveBatch,
  postSTOPickVerifyBasket,
} from 'redux-stores/actions';
import { AppDispatch } from 'redux-stores/store';

import S from '../STOPickItemPage.style';

const DEFAULT_NEW_PICK_ACTUAL = {
  basket: null,
  bin: '',
  bin_type: '',
  inventory_number: '',
  expiry_date: '',
  quantity: 0,
  is_verified: false,
};

interface STOPickingProcessFormProps {
  stoItem: STOItem;
  itemIdx: number;
  setIsAllBasketVerified: (isAllBasketVerified: boolean) => void;
}

export const STOPickingProcessForm: React.FC<STOPickingProcessFormProps> = ({
  stoItem,
  itemIdx,
  setIsAllBasketVerified,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentSTOItem, setCurrentSTOItem] = useState<STOItem | null>(null);
  const [currentPickActual, setCurrentPickActual] = useState<
    STOProcessedItem[]
  >([]);

  /** ***** FUNCTIONS ***** */
  const _checkBasketVerified = (pickActual: STOProcessedItem[]) => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < pickActual.length; i++) {
      if (!pickActual[i].is_verified) return setIsAllBasketVerified(false);
    }
    return setIsAllBasketVerified(true);
  };

  const handleAddBatch = (): void => {
    const newPickActual = structuredClone(currentPickActual);
    newPickActual.push(DEFAULT_NEW_PICK_ACTUAL);
    setCurrentPickActual(newPickActual);
    _checkBasketVerified(newPickActual);
  };

  const handleRemoveBatch = (): void => {
    const newPickActual = structuredClone(currentPickActual);
    const removedSource = newPickActual.pop();

    if (removedSource && removedSource.bin && removedSource.inventory_number) {
      postSTOPickRemoveBatch(
        {
          sto_number: stoItem.sto_number,
          sku_code: stoItem.sku_code,
          item: removedSource,
          item_idx: itemIdx,
        },
        dispatch,
      );
    }
    setCurrentPickActual(newPickActual);
    _checkBasketVerified(newPickActual);
  };

  /** ***** DEBOUNCE FUNCTIONS ***** */
  const debouncedVerifyBasket = useDebouncedCallback(
    (params: {
      stoNumber: string;
      skuCode: string;
      item: STOProcessedItem;
      oldItems: STOProcessedItem[];
    }) => {
      postSTOPickVerifyBasket(
        {
          sto_number: params.stoNumber,
          sku_code: params.skuCode,
          item: params.item,
          item_idx: itemIdx,
        },
        dispatch,
      ).then((result) => {
        if (!result) {
          setCurrentPickActual(params.oldItems);
          _checkBasketVerified(params.oldItems);
        }
      });
    },
    500,
    {
      maxWait: 2000,
    },
  );
  const debouncedRemoveBatch = useDebouncedCallback(
    (params: {
      stoNumber: string;
      skuCode: string;
      item: STOProcessedItem;
    }) => {
      postSTOPickRemoveBatch(
        {
          sto_number: params.stoNumber,
          sku_code: params.skuCode,
          item: params.item,
          item_idx: itemIdx,
        },
        dispatch,
      );
    },
    500,
    {
      maxWait: 2000,
    },
  );
  const debouncedProductDetail = useDebouncedCallback(
    async (params: {
      binNumber: string;
      skuCode: string;
      inventoryNumber: string;
      pickIndex: number;
    }) => {
      const { pickIndex, ...restParams } = params;
      const result = await getSTOPickProductDetail(restParams, dispatch);
      const clonedPicked = structuredClone(currentPickActual);
      if (result) {
        clonedPicked[pickIndex].expiry_date = result.expiry_date.toString();
        clonedPicked[pickIndex].bin = result.bin;
        clonedPicked[pickIndex].inventory_number = result.inventory_number;
        clonedPicked[pickIndex].bin_type = result.bin_type;
      } else {
        clonedPicked[pickIndex].bin = '';
        clonedPicked[pickIndex].inventory_number = '';
        clonedPicked[pickIndex].bin_type = '';
      }
      setCurrentPickActual(clonedPicked);
    },
    500,
    { maxWait: 2000, trailing: true },
  );
  const debouncedFocusNextField = useDebouncedCallback((selectorId: string) => {
    const nextInput = document.querySelector<HTMLInputElement>(selectorId);
    if (nextInput) nextInput.focus();
  }, 500);
  const debouncedPickItem = useDebouncedCallback(
    (params: {
      stoNumber: string;
      skuCode: string;
      item: STOProcessedItem;
    }) => {
      postSTOPickedItem(
        {
          sto_number: params.stoNumber,
          sku_code: params.skuCode,
          item: params.item,
          item_idx: itemIdx,
        },
        dispatch,
      );
    },
    500,
    {
      maxWait: 2000,
    },
  );

  /** ***** USE EFFECT ***** */
  useEffect(() => {
    if (
      !currentSTOItem ||
      stoItem.sku_code !== currentSTOItem.sku_code ||
      stoItem.sto_number !== currentSTOItem.sto_number
    ) {
      const defaultPickActual: STOProcessedItem[] = [DEFAULT_NEW_PICK_ACTUAL];
      setCurrentPickActual(
        stoItem.pick_actual.length > 0
          ? stoItem.pick_actual
          : defaultPickActual,
      );
      setCurrentSTOItem(stoItem);
    }
  }, [currentSTOItem, stoItem]);

  const renderForm = () =>
    currentPickActual.map((info, idx) => {
      const onFieldChange = (
        type: keyof STOProcessedItem,
        value: string,
        nextField?: string,
      ): void => {
        const oldPickActual = structuredClone(currentPickActual); // for error purpose, set value back to previous value
        const clonedPicked = structuredClone(currentPickActual);
        const prevValue = clonedPicked[idx][type];
        const cleanNewValue = value.trim();

        if (
          (type === 'bin' || type === 'inventory_number') &&
          clonedPicked[idx].quantity !== 0
        ) {
          const newBatch: STOProcessedItem = {
            bin: '',
            inventory_number: '',
            bin_type: '',
            expiry_date: '',
            quantity: 0,
            basket: clonedPicked[idx].basket,
            is_verified: false,
          };
          newBatch[type] = cleanNewValue;
          const replaced = clonedPicked.splice(idx, 1, newBatch);
          if (newBatch.bin === '' || newBatch.inventory_number === '') {
            debouncedRemoveBatch({
              stoNumber: stoItem.sto_number,
              skuCode: stoItem.sku_code,
              item: replaced[0],
            });
          }
        } else if (type === 'bin' || type === 'inventory_number') {
          clonedPicked[idx][type] = cleanNewValue;
        } else if (
          /*
          data type number
          need to be in this chained OR format, so the else block could executed properly
          */
          type === 'quantity' ||
          type === 'basket'
        ) {
          if (!Number.isNaN(+cleanNewValue)) {
            if (type === 'quantity' && cleanNewValue !== '') {
              // to determine how many quantity can a field have depend on total quantity
              const maxQuantity =
                stoItem.quantity -
                stoItem.pick_actual.reduce((sum, pick) => {
                  if (
                    pick.bin === clonedPicked[idx].bin &&
                    pick.inventory_number === clonedPicked[idx].inventory_number
                  ) {
                    return sum;
                  } else {
                    return sum + pick.quantity;
                  }
                }, 0);
              const fieldQuantity =
                cleanNewValue && +cleanNewValue > 0
                  ? Math.floor(+cleanNewValue)
                  : 0;
              clonedPicked[idx].quantity =
                fieldQuantity > maxQuantity ? maxQuantity : fieldQuantity;
            } else if (type === 'basket') {
              clonedPicked[idx].basket = +cleanNewValue;
              clonedPicked[idx].is_verified = !!+cleanNewValue;
            }
          }
        }

        // update state
        setCurrentPickActual(clonedPicked);
        _checkBasketVerified(clonedPicked);

        /* debounce after state assignment to prevent race condition */
        const {
          bin,
          inventory_number: inventoryNumber,
          quantity,
          basket,
        } = clonedPicked[idx];
        if (
          (type === 'bin' || type === 'inventory_number') &&
          bin &&
          inventoryNumber
        ) {
          debouncedProductDetail({
            binNumber: bin,
            inventoryNumber,
            skuCode: stoItem.sku_code,
            pickIndex: idx,
          });
        }
        if (bin && inventoryNumber && quantity && type !== 'basket') {
          debouncedPickItem({
            skuCode: stoItem.sku_code,
            stoNumber: stoItem.sto_number,
            item: clonedPicked[idx],
          });
        } else if (quantity === 0 && bin !== '' && inventoryNumber !== '') {
          debouncedRemoveBatch({
            stoNumber: stoItem.sto_number,
            skuCode: stoItem.sku_code,
            item: clonedPicked[idx],
          });
        } else if (type === 'basket') {
          debouncedVerifyBasket({
            stoNumber: stoItem.sto_number,
            skuCode: stoItem.sku_code,
            item: clonedPicked[idx],
            oldItems: oldPickActual,
          });
        }

        if (
          nextField &&
          (value.endsWith('\t') ||
            value.endsWith('\n') ||
            (!prevValue && value.length > 1))
        ) {
          debouncedFocusNextField(
            `input[id="field-sto-pick${nextField}-${basket}-${idx}"]`,
          );
        }
      };

      const clearField = (field: keyof STOProcessedItem) => {
        const clonedPicked = structuredClone(currentPickActual);
        if (
          field === 'bin' ||
          field === 'inventory_number' ||
          field === 'expiry_date'
        ) {
          // remove batch
          if (
            clonedPicked[idx].bin !== '' &&
            clonedPicked[idx].inventory_number !== ''
          ) {
            const replaced = structuredClone(clonedPicked[idx]);
            debouncedRemoveBatch({
              stoNumber: stoItem.sto_number,
              skuCode: stoItem.sku_code,
              item: replaced,
            });
          }
          clonedPicked[idx][field] = '';
          clonedPicked[idx].quantity = 0;
        } else if (field === 'is_verified') {
          clonedPicked[idx][field] = false;
        } else if (field === 'quantity') {
          if (
            clonedPicked[idx].bin !== '' &&
            clonedPicked[idx].inventory_number !== ''
          ) {
            debouncedRemoveBatch({
              item: clonedPicked[idx],
              stoNumber: stoItem.sto_number,
              skuCode: stoItem.sku_code,
            });
          }
          clonedPicked[idx][field] = 0;
        }

        setCurrentPickActual(clonedPicked);
      };

      return (
        <Grid container style={{ padding: '8px 0' }}>
          <Grid item xs={1} />
          <Grid item xs={11}>
            <Grid container>
              <Grid item xs={5}>
                <p>Bin</p>
              </Grid>
              <S.PickProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  placeholder="-"
                  value={info.bin}
                  onChange={(event) =>
                    onFieldChange('bin', event.target.value, 'inventory_number')
                  }
                  id={`field-sto-pickbin-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => clearField('bin')}>
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.PickProcessInputGrid>
              <Grid item xs={5}>
                <p>Batch Number</p>
              </Grid>
              <S.PickProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  placeholder="-"
                  value={info.inventory_number}
                  onChange={(event) =>
                    onFieldChange(
                      'inventory_number',
                      event.target.value,
                      'quantity',
                    )
                  }
                  id={`field-sto-pickbatch-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => clearField('inventory_number')}
                        >
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.PickProcessInputGrid>
              <Grid item xs={5}>
                <p>Bin Type</p>
              </Grid>
              <S.PickProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  defaultValue={null}
                  value={info.bin_type || '-'}
                  disabled
                  id={`field-sto-bintype-${info.bin_type}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton disabled>
                          <Clear style={{ color: '#fff' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.PickProcessInputGrid>
              <Grid item xs={5}>
                <p>Expiry Date</p>
              </Grid>
              <S.PickProcessInputGrid item xs={6}>
                <DateField
                  variant="outlined"
                  format="dd/MM/yyyy"
                  defaultValue={null}
                  value={
                    info.expiry_date ? DateTime.fromISO(info.expiry_date) : null
                  }
                  disabled
                  id={`field-sto-pickexpiry-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton disabled>
                          <Clear style={{ color: '#fff' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.PickProcessInputGrid>
              <Grid item xs={5}>
                <p>Quantity Picked</p>
              </Grid>
              <S.PickProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  placeholder="0"
                  type="number"
                  value={info.quantity || ''}
                  onChange={(event) =>
                    onFieldChange('quantity', event.target.value, 'basket')
                  }
                  id={`field-sto-pickquantity-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => clearField('quantity')}>
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.PickProcessInputGrid>
              <Grid item xs={5}>
                <p>Basket</p>
              </Grid>
              <S.PickProcessInputGrid item xs={6}>
                <TextField
                  variant="outlined"
                  placeholder="0"
                  type="number"
                  value={info.basket || ''}
                  onChange={(event) => {
                    onFieldChange('basket', event.target.value);
                  }}
                  id={`field-sto-pickbasket-verification-${info.basket}-${idx}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => onFieldChange('basket', '0')}
                        >
                          <Clear style={{ color: colors.PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  }}
                />
              </S.PickProcessInputGrid>
            </Grid>
          </Grid>
        </Grid>
      );
    });

  return (
    <>
      {renderForm()}
      <Grid container style={{ marginTop: 8 }}>
        <Grid item xs={6} style={{ textAlign: 'center' }}>
          <S.RedButtonText
            onClick={handleRemoveBatch}
            disabled={currentPickActual.length === 1}
            data-testid="BtnSTTaskSourceRemoveBatch"
          >
            REMOVE BATCH
          </S.RedButtonText>
        </Grid>
        <Grid
          item
          xs={6}
          style={{ textAlign: 'right' }}
          data-testid="BtnSTTaskSourceAddBatch"
        >
          <S.BlueButtonText onClick={handleAddBatch}>
            <Add /> ADD BATCH
          </S.BlueButtonText>
        </Grid>
      </Grid>
    </>
  );
};
